import { addDays as addDaysFns, addMonths, addYears, differenceInDays, format as formatFns, isAfter as isAfterFns, isBefore as isBeforeFns, isSameDay, isValid, parse, parseISO, startOfDay, } from 'date-fns';
import { isDateString } from '@data/api/types/native';
const INVALID_DATE = 'Invalid date';
const MOMENT_TOKEN_TO_DATE_FNS = {
    A: 'a',
    D: 'd',
    DD: 'dd',
    YY: 'yy',
    YYYY: 'yyyy',
};
const MOMENT_TOKEN_PATTERN = /YYYY|YY|DD|D|A|[^A-Za-z]+|[A-Za-z]+/g;
const patternCache = new Map();
// Property settings store patterns as `YYYY-MM-DD` / `hh:mm A`; date-fns expects unicode tokens
function toDateFnsPattern(pattern) {
    var _a;
    const cached = patternCache.get(pattern);
    if (cached)
        return cached;
    const translated = ((_a = pattern.match(MOMENT_TOKEN_PATTERN)) !== null && _a !== void 0 ? _a : [])
        .map((token) => { var _a; return (_a = MOMENT_TOKEN_TO_DATE_FNS[token]) !== null && _a !== void 0 ? _a : token; })
        .join('');
    patternCache.set(pattern, translated);
    return translated;
}
// Date-only strings resolve to local midnight, not UTC
function toDate(date) {
    if (date === undefined)
        return new Date();
    if (typeof date === 'number')
        return new Date(date);
    if (typeof date === 'string')
        return parseISO(date);
    return date;
}
const getDateOffset = (dayOffset = 0, monthOffset = 0, yearOffset = 0, baseDate = 0) => {
    let date = startOfDay(baseDate === 0 ? new Date() : toDate(baseDate));
    if (dayOffset !== 0)
        date = addDaysFns(date, dayOffset);
    if (monthOffset !== 0)
        date = addMonths(date, monthOffset);
    if (yearOffset !== 0)
        date = addYears(date, yearOffset);
    return date;
};
const addDays = (date, days) => days === 0 ? date : toDateString(addDaysFns(toDate(date), days));
const getNextDay = (date) => getDateOffset(1, 0, 0, date);
const getPrevDay = (date) => getDateOffset(-1, 0, 0, date);
const getIntervalArray = (start, end, { includeEndDate = true } = {}) => {
    const dates = [];
    let currentDate = start;
    while (currentDate <= end) {
        if (currentDate !== end || includeEndDate) {
            dates.push(currentDate);
        }
        currentDate = addDays(currentDate, 1);
    }
    return dates;
};
function format(date, pattern = 'YYYY-MM-DD') {
    const value = toDate(date);
    if (!isValid(value))
        return INVALID_DATE;
    return formatFns(value, toDateFnsPattern(pattern));
}
function formatTime(date, is12Hour) {
    return format(date, is12Hour ? 'hh:mm A' : 'HH:mm');
}
function fromIso(dateStr, options = {}) {
    if (!dateStr || !isDateString(dateStr)) {
        return undefined;
    }
    // @TECHDEBT: We may need to fix this in the future for a better handling of different timezones
    return new Date(`${dateStr}${options.startOfDay ? 'T00:00:00' : ''}`);
}
function fromString(date, pattern = 'YYYY-MM-DD') {
    return parse(date, toDateFnsPattern(pattern), new Date());
}
function getMonth(date) {
    return date.getMonth() + 1;
}
function getYear(date) {
    return date.getFullYear();
}
// Whole months plus a linear fraction of the surrounding month, truncated toward
// zero; anchoring on the later day-of-month keeps Jan 31 ↔ Feb 29 at one month
function getMonthDiff(date1, date2) {
    if (!date2)
        date2 = getToday();
    if (date1.getDate() < date2.getDate())
        return -getMonthDiff(date2, date1) || 0;
    const wholeMonths = (date2.getFullYear() - date1.getFullYear()) * 12 +
        (date2.getMonth() - date1.getMonth());
    const anchor = addMonths(date1, wholeMonths);
    const remainder = date2.getTime() - anchor.getTime();
    const neighbour = addMonths(date1, wholeMonths + (remainder < 0 ? -1 : 1));
    const fraction = remainder / Math.abs(neighbour.getTime() - anchor.getTime());
    return Math.trunc(-(wholeMonths + fraction)) || 0;
}
function getDiff(end, start) {
    if (!start)
        start = getToday();
    return differenceInDays(toDate(end), toDate(start));
}
function getEndOfMonthDate(date, monthOffset = 1) {
    return new Date(date.getFullYear(), date.getMonth() + monthOffset, 0);
}
function getStartOfMonthDate(date, monthOffset = 0) {
    return new Date(date.getFullYear(), date.getMonth() + monthOffset, 1);
}
function isBetween(date, startDate, endDate) {
    return isSameOrAfter(date, startDate) && isSameOrBefore(date, endDate);
}
function isEqual(date1, date2) {
    return isSameDay(toDate(date1), toDate(date2));
}
function isBefore(date1, date2) {
    return isBeforeFns(startOfDay(toDate(date1)), startOfDay(toDate(date2)));
}
function isSameOrBefore(date1, date2) {
    return isBefore(date1, date2) || isEqual(date1, date2);
}
function isAfter(date1, date2) {
    return isAfterFns(startOfDay(toDate(date1)), startOfDay(toDate(date2)));
}
function isSameOrAfter(date1, date2) {
    return isAfter(date1, date2) || isEqual(date1, date2);
}
function isToday(date) {
    return isEqual(date, new Date());
}
function isPastDate(date) {
    return isBefore(date, new Date());
}
function max(date1, date2) {
    return isBefore(date1, date2) ? date2 : date1;
}
function min(date1, date2) {
    return isBefore(date1, date2) ? date1 : date2;
}
function getDateString(date, locale = 'en', size = 'md') {
    if (!date)
        return '-';
    if (['pt-BR', 'pt-PT'].includes(locale)) {
        const day = new Intl.DateTimeFormat(locale, {
            day: 'numeric',
        }).format(date);
        const month = new Intl.DateTimeFormat(locale, {
            month: 'short',
        })
            .format(date)
            .replace('.', '');
        const year = new Intl.DateTimeFormat(locale, {
            year: 'numeric',
        }).format(date);
        return `${day} ${month} ${year}`;
    }
    const formatOptions = {
        day: 'numeric',
        month: 'short',
        year: size === 'sm' ? undefined : 'numeric',
    };
    return Intl.DateTimeFormat(locale, formatOptions).format(date);
}
function getToday() {
    return startOfDay(new Date());
}
const toDateString = (date) => format(date);
function getDayAbbreviation(date, translationFunction) {
    const dayIndex = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const dayKeys = [
        'days/three-letter/sunday',
        'days/three-letter/monday',
        'days/three-letter/tuesday',
        'days/three-letter/wednesday',
        'days/three-letter/thursday',
        'days/three-letter/friday',
        'days/three-letter/saturday',
    ];
    return translationFunction(dayKeys[dayIndex]);
}
const TimeUtils = {
    addDays,
    format,
    formatTime,
    fromIso,
    fromString,
    getDateOffset,
    getDateString,
    getDayAbbreviation,
    getDiff,
    getEndOfMonthDate,
    getIntervalArray,
    getMonth,
    getMonthDiff,
    getNextDay,
    getPrevDay,
    getStartOfMonthDate,
    getToday,
    getYear,
    isAfter,
    isBefore,
    isBetween,
    isEqual,
    isPastDate,
    isSameOrAfter,
    isSameOrBefore,
    isToday,
    max,
    min,
    toDateString,
};
export default TimeUtils;
